/**
 * El editor de un cuestionario.
 *
 * Reordenar se hace con **botones de subir y bajar**, no arrastrando. RF-004 pide «reordenar», no
 * «arrastrar»; los botones cumplen RF-004 y RF-005 enteros, son accesibles por definición y no
 * cuestan ninguna dependencia. El arrastre con ratón queda como mejora posterior si el uso real
 * lo pide.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import {
  assessQuiz,
  exchangeFileName,
  findLongCodeLines,
  serializeExchangeFile,
  MAX_CODE_COLUMNS,
  defaultQuestionTypeId,
  type QuestionIssues,
  type RawQuestion,
  PLATFORM_SETTINGS,
  MAX_HINT_LENGTH,
  MAX_OPTION_LENGTH,
  MAX_PROMPT_LENGTH,
  type SessionProfile,
} from '@clica/shared';
import {
  createOption,
  createQuestion,
  deleteOption,
  deleteQuestion,
  getQuiz,
  listOptions,
  listQuestions,
  reorderQuestions,
  saveWithVersion,
  type Option,
  type Question,
  type Quiz,
} from '../content/api.js';
import { RowSaver, type SaveState } from '../content/autosave.js';
import { ImagenEditor } from '../components/imagen-editor.js';
import { VistaPrevia } from '../components/vista-previa.js';
import { FichaOpcion, letterFor } from '../components/ficha-opcion.js';
import { AvisoConflicto, IndicadorGuardado } from '../components/indicador-guardado.js';
import { Aviso } from '../components/sistema/aviso.js';
import { useAvisos } from '../components/sistema/avisos.js';
import { HistorialDePartidas } from '../components/historial-de-partidas.js';
import { Marco } from '../components/sistema/marco.js';
import { Boton } from '../components/sistema/boton.js';
import { DialogoConfirmacion } from '../components/sistema/dialogo-confirmacion.js';
import { Glifo } from '../components/sistema/glifo.js';
import { Rotulo } from '../components/sistema/rotulo.js';

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 4;

/** Un campo de texto que se guarda solo tras una pausa. */
function CampoAutoguardado({
  id,
  label,
  value,
  rows,
  maximo,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  rows: number;
  /** Cuántos caracteres admite. El contador solo aparece cuando queda poco. */
  maximo: number;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const restantes = maximo - value.length;
  // El contador no está siempre: en un campo vacío es ruido, y quien escribe dos líneas no
  // necesita que le recuerden que le quedan setecientos caracteres.
  const avisa = restantes <= maximo * 0.2;

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <Rotulo htmlFor={id}>{label}</Rotulo>
        {avisa && (
          <span
            className={[
              'text-micro tabular-nums',
              restantes < 0 ? 'text-ko' : 'text-text-dim',
            ].join(' ')}
            // Se anuncia con cortesía al acercarse al final, no en cada tecla.
            aria-live="polite"
          >
            {t('editor.charactersLeft', { count: restantes })}
          </span>
        )}
      </div>
      <textarea
        id={id}
        value={value}
        rows={rows}
        maxLength={maximo}
        onChange={(e) => onChange(e.target.value)}
        className="text-body rounded-chip border-line bg-surface w-full border-2 px-3 py-2.5 font-mono"
      />
    </div>
  );
}

function EditorOpciones({
  options,
  problems,
  onChange,
  onAdd,
  onRemove,
  ocupado,
}: {
  options: readonly Option[];
  problems: readonly string[];
  onChange: (id: string, patch: Record<string, unknown>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  ocupado: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const groupName = `correcta-${options[0]?.question_id ?? 'x'}`;

  return (
    <fieldset className="mt-4">
      <legend className="mb-2">
        <Rotulo>{t('editor.options')}</Rotulo>
      </legend>

      <ul className="flex flex-col gap-2">
        {options.map((option) => {
          const letter = letterFor(option.position);
          return (
            <li key={option.id} className="flex items-center gap-3">
              <FichaOpcion position={option.position} size={36} />

              {/* Grupo de radios: exactamente una correcta. Marcar otra desmarca la anterior sin
                  que haya que escribir una línea para ello (RF-007). */}
              <label className="flex min-h-11 shrink-0 cursor-pointer items-center gap-1.5">
                <input
                  type="radio"
                  name={groupName}
                  checked={option.is_correct}
                  onChange={() => onChange(option.id, { is_correct: true })}
                  className="accent-ok h-5 w-5"
                />
                <span className="sr-only">{t('editor.markCorrect', { letter })}</span>
                {option.is_correct ? (
                  <Glifo nombre="correcto" size={18} className="text-ok" />
                ) : null}
              </label>

              <label className="flex-1">
                <span className="sr-only">{t('editor.optionText', { letter })}</span>
                <input
                  value={option.text_md ?? ''}
                  maxLength={MAX_OPTION_LENGTH}
                  onChange={(e) => onChange(option.id, { text_md: e.target.value })}
                  aria-invalid={
                    problems.includes(`emptyOption:${option.position}`) ||
                    problems.includes(`optionTooLong:${option.position}`)
                  }
                  className="text-body aria-[invalid=true]:border-ko rounded-chip border-line bg-surface w-full border-2 px-3 py-2.5"
                />
              </label>

              {/* Quitar solo se ofrece mientras sobren opciones: por debajo de dos, la pregunta
                  deja de poder jugarse, y un botón que rompe lo que toca no debería estar ahí. */}
              <Boton
                variante="fantasma"
                disabled={options.length <= MIN_OPTIONS || ocupado}
                onClick={() => onRemove(option.id)}
                title={t('editor.removeOption', { letter })}
              >
                <Glifo nombre="cerrar" size={18} />
                <span className="sr-only">{t('editor.removeOption', { letter })}</span>
              </Boton>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 flex items-center gap-3">
        <Boton
          variante="secundaria"
          disabled={options.length >= MAX_OPTIONS || ocupado}
          cargando={ocupado}
          onClick={onAdd}
        >
          {t('editor.addOption')}
        </Boton>
        <Rotulo>{t('editor.optionCount', { count: options.length, max: MAX_OPTIONS })}</Rotulo>
      </div>

      {options.length < MIN_OPTIONS && (
        <p className="text-ko text-body mt-2">{t('editor.minOptions')}</p>
      )}
    </fieldset>
  );
}

/**
 * Los ajustes de una pregunta.
 *
 * Existían en el esquema, en el contrato y en el archivo de exportación, pero **no había forma de
 * tocarlos desde la interfaz**: la única vía era exportar el cuestionario, editar el JSON a mano
 * y volver a importarlo. Sus etiquetas llevaban traducidas a tres idiomas desde el principio, sin
 * un solo uso.
 *
 * Van plegados porque son la excepción: lo normal es que una pregunta herede del cuestionario, y
 * el campo vacío significa exactamente eso.
 */
function AjustesPregunta({
  question,
  quizSettings,
  onChange,
}: {
  question: Question;
  quizSettings: Record<string, unknown>;
  onChange: (id: string, patch: Record<string, unknown>) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const tienePista = (question.hint_md ?? '').trim() !== '';

  /**
   * Lo que se aplicaría si el campo se deja vacío: del cuestionario, o de la plataforma.
   *
   * `default_settings` es JSON sin tipar, así que puede traer cualquier cosa. Solo se acepta un
   * valor primitivo; cualquier otra cosa se ignora y manda la constante de la plataforma, que es
   * lo que hace el servidor de todas formas.
   */
  function heredado(clave: 'time_limit_s' | 'shuffle_options' | 'points_multiplier'): string {
    const delQuiz: unknown = quizSettings[clave];
    const esUtil =
      typeof delQuiz === 'number' || typeof delQuiz === 'boolean' || typeof delQuiz === 'string';
    return String(esUtil ? delQuiz : PLATFORM_SETTINGS[clave]);
  }

  function numero(campo: string, valor: string): void {
    // Vacío es nulo, no cero: nulo hereda y cero sería un tiempo de cero segundos.
    const limpio = valor.trim();
    onChange(question.id, { [campo]: limpio === '' ? null : Number(limpio) });
  }

  return (
    <details className="rounded-card border-line mt-4 border-2">
      <summary className="text-micro font-display cursor-pointer px-4 py-3 font-bold uppercase">
        {t('editor.settings')}
      </summary>

      <div className="grid gap-4 px-4 pt-1 pb-4 sm:grid-cols-2">
        <div>
          <Rotulo htmlFor={`limite-${question.id}`} className="mb-1 block">
            {t('editor.timeLimit')}
          </Rotulo>
          <input
            id={`limite-${question.id}`}
            type="number"
            inputMode="numeric"
            min={5}
            max={300}
            value={question.time_limit_s ?? ''}
            placeholder={heredado('time_limit_s')}
            onChange={(e) => numero('time_limit_s', e.target.value)}
            className="text-body rounded-chip border-line bg-surface w-full border-2 px-3 py-2.5"
          />
          <p className="text-micro text-text-dim mt-1">
            {t('editor.inheritedValue', { value: heredado('time_limit_s') })}
          </p>
        </div>

        <div>
          <Rotulo htmlFor={`pista-tras-${question.id}`} className="mb-1 block">
            {t('editor.hintAfter')}
          </Rotulo>
          <input
            id={`pista-tras-${question.id}`}
            type="number"
            inputMode="numeric"
            min={0}
            max={300}
            disabled={!tienePista}
            value={question.hint_after_s ?? ''}
            onChange={(e) => numero('hint_after_s', e.target.value)}
            className="text-body rounded-chip border-line bg-surface w-full border-2 px-3 py-2.5 disabled:opacity-50"
          />
          <p className="text-micro text-text-dim mt-1">{t('editor.hintAfterHelp')}</p>
        </div>

        <div>
          <Rotulo htmlFor={`multiplicador-${question.id}`} className="mb-1 block">
            {t('editor.pointsMultiplier')}
          </Rotulo>
          <input
            id={`multiplicador-${question.id}`}
            type="number"
            inputMode="decimal"
            min={0.5}
            max={5}
            step={0.5}
            value={question.points_multiplier ?? ''}
            placeholder={heredado('points_multiplier')}
            onChange={(e) => numero('points_multiplier', e.target.value)}
            className="text-body rounded-chip border-line bg-surface w-full border-2 px-3 py-2.5"
          />
        </div>

        <label className="text-body flex min-h-11 cursor-pointer items-center gap-2 self-end">
          <input
            type="checkbox"
            checked={question.shuffle_options ?? heredado('shuffle_options') === 'true'}
            onChange={(e) => onChange(question.id, { shuffle_options: e.target.checked })}
            className="accent-ok h-6 w-6"
          />
          {t('editor.shuffleOptions')}
        </label>
      </div>
    </details>
  );
}

function TarjetaPregunta({
  question,
  options,
  index,
  total,
  issues,
  onMove,
  onDelete,
  onQuestionChange,
  onOptionChange,
  onOptionAdd,
  onOptionRemove,
  ocupado,
  quizSettings,
}: {
  question: Question;
  options: readonly Option[];
  index: number;
  total: number;
  issues: QuestionIssues | undefined;
  onMove: (index: number, delta: number) => void;
  onDelete: (id: string) => void;
  onQuestionChange: (id: string, patch: Record<string, unknown>) => void;
  onOptionChange: (id: string, patch: Record<string, unknown>) => void;
  onOptionAdd: (questionId: string) => void;
  onOptionRemove: (questionId: string, optionId: string) => void;
  ocupado: boolean;
  quizSettings: Record<string, unknown>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const prompt = question.prompt_md ?? '';
  const longLines = findLongCodeLines(prompt);

  const problemKeys = (issues?.problems ?? []).map((p) =>
    p.optionPosition === undefined ? p.reason : `${p.reason}:${p.optionPosition}`,
  );

  return (
    <li id={`pregunta-${question.id}`} className="rounded-card border-line bg-surface border-2 p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-display text-h3 font-bold">
          {t('editor.questionNumber', { number: index + 1 })}
        </h3>
        <div className="flex shrink-0 gap-1">
          {/* Estos tres medían 26x30 px, por debajo del área táctil mínima, y eran los que más
              se usan al ordenar un cuestionario largo. */}
          <Boton
            variante="secundaria"
            soloIcono
            onClick={() => onMove(index, -1)}
            disabled={index === 0}
          >
            <Glifo nombre="arriba" size={18} />
            <span className="sr-only">{t('editor.moveUp')}</span>
          </Boton>
          <Boton
            variante="secundaria"
            soloIcono
            onClick={() => onMove(index, 1)}
            disabled={index === total - 1}
          >
            <Glifo nombre="abajo" size={18} />
            <span className="sr-only">{t('editor.moveDown')}</span>
          </Boton>
          <Boton variante="destructiva" soloIcono onClick={() => onDelete(question.id)}>
            <Glifo nombre="cerrar" size={18} />
            <span className="sr-only">{t('editor.delete')}</span>
          </Boton>
        </div>
      </div>

      {/*
        `min-w-0` en las columnas de la cuadrícula.

        Una columna de `grid` mide `auto`, que es `minmax(auto, ...)`: su mínimo es el contenido,
        no cero. Con un `textarea` dentro —que trae su propio ancho mínimo por el número de
        columnas— la columna se negaba a encoger y empujaba la página once píxeles a 320. Es el
        mismo `min-w-0` que hace falta en un hijo de `flex` para que `truncate` funcione, por la
        misma razón, y se olvida igual de a menudo.
      */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-3">
          <CampoAutoguardado
            id={`enunciado-${question.id}`}
            label={t('editor.prompt')}
            value={prompt}
            rows={5}
            maximo={MAX_PROMPT_LENGTH}
            onChange={(value) => onQuestionChange(question.id, { prompt_md: value })}
          />
          {/* Aviso no bloqueante: informa, no impide guardar. */}
          {longLines.length > 0 && (
            <p className="text-body text-text-dim flex items-center gap-2">
              <Glifo nombre="aviso" size={18} />
              {t('editor.longCodeLines', { max: MAX_CODE_COLUMNS })}
            </p>
          )}
          <CampoAutoguardado
            id={`pista-${question.id}`}
            label={t('editor.hint')}
            value={question.hint_md ?? ''}
            rows={2}
            maximo={MAX_HINT_LENGTH}
            onChange={(value) => onQuestionChange(question.id, { hint_md: value })}
          />

          <ImagenEditor
            quizId={question.quiz_id}
            value={{
              path: question.image_path,
              alt: question.image_alt,
              width: question.image_width,
              height: question.image_height,
            }}
            onChange={(image) =>
              onQuestionChange(question.id, {
                image_path: image.path,
                image_alt: image.alt,
                image_width: image.width,
                image_height: image.height,
              })
            }
          />
        </div>

        <VistaPrevia source={prompt} />
      </div>

      <AjustesPregunta
        question={question}
        quizSettings={quizSettings}
        onChange={onQuestionChange}
      />

      <EditorOpciones
        options={options}
        problems={problemKeys}
        onChange={onOptionChange}
        onAdd={() => onOptionAdd(question.id)}
        onRemove={(optionId) => onOptionRemove(question.id, optionId)}
        ocupado={ocupado}
      />
    </li>
  );
}

function ResumenValidacion({
  issues,
  hasQuestions,
}: {
  issues: readonly QuestionIssues[];
  hasQuestions: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (!hasQuestions) {
    return (
      <div className="rounded-card border-line mt-6 border-2 border-dashed p-5">
        <p className="text-body text-text-dim">{t('validation.noQuestions')}</p>
      </div>
    );
  }

  if (issues.length === 0) {
    return (
      <div className="rounded-card border-ok bg-surface mt-6 border-2 p-5">
        <p className="font-display text-ok flex items-center gap-2 font-bold">
          <Glifo nombre="correcto" size={20} />
          {t('validation.ready')}
        </p>
      </div>
    );
  }

  return (
    <section
      aria-labelledby="titulo-validacion"
      className="rounded-card border-ko bg-surface mt-6 border-2 p-5"
    >
      <h2 id="titulo-validacion" className="font-display flex items-center gap-2 font-bold">
        <Glifo nombre="aviso" size={20} className="text-ko" />
        {t('validation.title')}
      </h2>
      {/* Cada problema enlaza con su pregunta. La clave estaba traducida en los tres idiomas y
          no la usaba nadie: el resumen decía qué pasa y no dónde. */}
      <ul className="text-body mt-2 flex flex-col gap-1">
        {issues.map((issue) =>
          issue.problems.map((problem, i) => (
            <li key={`${issue.questionId}-${i}`}>
              <a
                href={`#pregunta-${issue.questionId}`}
                className="font-bold underline underline-offset-4"
              >
                {t('validation.goToQuestion', { number: issue.position + 1 })}
              </a>{' '}
              —{' '}
              {t(`validation.${problem.reason}`, {
                letter:
                  problem.optionPosition === undefined ? '' : letterFor(problem.optionPosition),
              })}
            </li>
          )),
        )}
      </ul>
    </section>
  );
}

export function PantallaEditor({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  const { quizId = '' } = useParams();

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [questions, setQuestions] = useState<readonly Question[]>([]);
  const [options, setOptions] = useState<readonly Option[]>([]);
  /**
   * Un estado de guardado POR FILA.
   *
   * Antes había uno solo para toda la pantalla y todos los guardadores escribían en él, así que
   * ganaba el último que hablara: un «Guardado» de otra pregunta hacía desaparecer el aviso de
   * conflicto de la que sí lo tenía, y esa fila ya no volvía a intentarlo nunca. La persona se
   * quedaba con el trabajo sin guardar y con la pantalla diciendo que todo estaba bien.
   */
  const [estados, setEstados] = useState<ReadonlyMap<string, SaveState>>(new Map());
  const [ocupado, setOcupado] = useState(false);
  const [porBorrar, setPorBorrar] = useState<string | null>(null);
  const avisos = useAvisos();
  const savers = useRef(new Map<string, RowSaver<{ version: number }>>());

  /**
   * Cuatro situaciones que antes se veían igual.
   *
   * `load()` encadenaba tres consultas sin estado de carga ni `catch`, y `getQuiz` devuelve `null`
   * cuando no existe. Así, un cuestionario borrado, uno que todavía no ha llegado y uno cuya carga
   * ha fallado enseñaban los tres el mismo «Añade al menos una pregunta», que además es falso en
   * los tres casos.
   */
  const [estadoCarga, setEstadoCarga] = useState<'cargando' | 'fallo' | 'noExiste' | 'listo'>(
    'cargando',
  );

  const load = useCallback(async (): Promise<void> => {
    try {
      const loaded = await getQuiz(quizId);
      if (loaded === null) {
        setEstadoCarga('noExiste');
        return;
      }
      setQuiz(loaded);
      const qs = await listQuestions(quizId);
      setQuestions(qs);
      setOptions(await listOptions(qs.map((q) => q.id)));
      setEstadoCarga('listo');
    } catch (error) {
      console.error('No se ha podido cargar el cuestionario', error);
      setEstadoCarga('fallo');
    }
  }, [quizId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Al desmontar, se guarda lo pendiente y se sueltan los guardadores.
  useEffect(() => {
    const map = savers.current;
    return () => {
      for (const saver of map.values()) {
        saver.flush();
        saver.dispose();
      }
      map.clear();
    };
  }, []);

  /**
   * Avisa antes de cerrar la pestaña con algo sin guardar.
   *
   * El autoguardado espera 800 ms desde la última tecla. Quien escribe una frase y cierra sin
   * pensárselo la pierde entera, y la pierde en silencio. En toda la SPA no había ni un
   * `beforeunload`; `hasPendingChanges` estaba escrito y no lo consultaba nadie.
   *
   * El navegador enseña su propio texto, así que no hay nada que traducir: lo único que se puede
   * hacer es pedir que pregunte.
   */
  useEffect(() => {
    const map = savers.current;
    function alCerrar(evento: BeforeUnloadEvent): void {
      const pendiente = [...map.values()].some((saver) => saver.hasPendingChanges);
      if (!pendiente) return;
      evento.preventDefault();
    }

    window.addEventListener('beforeunload', alCerrar);
    return () => window.removeEventListener('beforeunload', alCerrar);
  }, []);

  function saverFor(
    table: 'questions' | 'options',
    id: string,
    version: number,
  ): RowSaver<{ version: number }> {
    const key = `${table}:${id}`;
    let saver = savers.current.get(key);
    if (saver === undefined) {
      saver = new RowSaver<{ version: number }>({
        table,
        id,
        initialVersion: version,
        onState: (estado) =>
          setEstados((actuales) => {
            const siguiente = new Map(actuales);
            siguiente.set(key, estado);
            return siguiente;
          }),
        onSaved: () => undefined,
      });
      savers.current.set(key, saver);
    }
    return saver;
  }

  function changeQuestion(id: string, patch: Record<string, unknown>): void {
    setQuestions((current) => current.map((q) => (q.id === id ? { ...q, ...patch } : q)));
    const version = questions.find((q) => q.id === id)?.version ?? 1;
    saverFor('questions', id, version).change(patch);
  }

  function changeOption(id: string, patch: Record<string, unknown>): void {
    const target = options.find((o) => o.id === id);
    if (target === undefined) return;

    setOptions((current) =>
      current.map((o) => {
        if (o.id === id) return { ...o, ...patch };
        // Marcar una correcta desmarca las demás de la misma pregunta (RF-007).
        if (patch['is_correct'] === true && o.question_id === target.question_id) {
          return { ...o, is_correct: false };
        }
        return o;
      }),
    );

    saverFor('options', id, target.version).change(patch);

    if (patch['is_correct'] === true) {
      for (const other of options) {
        if (other.question_id !== target.question_id || other.id === id) continue;
        if (!other.is_correct) continue;
        saverFor('options', other.id, other.version).change({ is_correct: false });
      }
    }
  }

  /**
   * Envuelve una acción que escribe: bloquea mientras dura y avisa si falla.
   *
   * Sin el bloqueo, dos pulsaciones seguidas en «Añadir pregunta» son dos inserciones que compiten
   * por la misma posición. Sin el aviso, un fallo de red no producía absolutamente nada: ni
   * mensaje, ni cambio, ni rastro, y la persona volvía a pulsar.
   */
  async function accion(tarea: () => Promise<void>): Promise<void> {
    if (ocupado) return;
    setOcupado(true);
    try {
      await tarea();
    } catch (error) {
      console.error('No se ha podido guardar el cambio', error);
      avisos.fallo(t('common.saveFailed'));
      // Se recarga para que la pantalla vuelva a decir lo que de verdad hay guardado: dejarla con
      // un orden o una lista que solo existe aquí es peor que el propio fallo.
      await load();
    } finally {
      setOcupado(false);
    }
  }

  async function addQuestion(): Promise<void> {
    await accion(async () => {
      // El tipo lo dice el registro: el editor no conoce ningún tipo concreto (principio VII).
      // La posición la calcula la API leyendo la mayor que haya: contar las preguntas fallaba en
      // cuanto se borraba una del medio y quedaba un hueco.
      await createQuestion(quizId, defaultQuestionTypeId());
      await load();
    });
  }

  async function anadirOpcion(questionId: string): Promise<void> {
    await accion(async () => {
      const suyas = options.filter((o) => o.question_id === questionId);
      if (suyas.length >= MAX_OPTIONS) return;
      await createOption(questionId, suyas.length);
      await load();
    });
  }

  async function quitarOpcion(questionId: string, optionId: string): Promise<void> {
    await accion(async () => {
      const suyas = options.filter((o) => o.question_id === questionId);
      if (suyas.length <= MIN_OPTIONS) return;

      const restantes = suyas.filter((o) => o.id !== optionId);
      // Si la que se quita era la correcta, la primera que queda toma el relevo: una pregunta sin
      // ninguna correcta no se puede jugar, y dejarla así sin decir nada es tender una trampa.
      const seVaLaCorrecta = suyas.find((o) => o.id === optionId)?.is_correct === true;
      await deleteOption(
        optionId,
        restantes.map((o) => o.id),
      );
      if (seVaLaCorrecta && restantes[0] !== undefined) {
        await saveWithVersion('options', restantes[0].id, restantes[0].version, {
          is_correct: true,
        });
      }
      await load();
    });
  }

  async function move(index: number, delta: number): Promise<void> {
    const target = index + delta;
    const a = questions[index];
    const b = questions[target];
    if (a === undefined || b === undefined) return;

    const anterior = questions;
    const next = [...questions];
    next[index] = b;
    next[target] = a;
    setQuestions(next);

    try {
      await reorderQuestions(next.map((q) => q.id));
    } catch (error) {
      // Se devuelve la pantalla a lo que hay guardado: quedarse con un orden que solo existe aquí
      // mientras el indicador dice «Guardado» es mentir sobre lo que ha pasado.
      console.error('No se ha podido reordenar', error);
      setQuestions(anterior);
      avisos.fallo(t('common.saveFailed'));
    }
  }

  async function remove(id: string): Promise<void> {
    setPorBorrar(null);
    await accion(async () => {
      await deleteQuestion(
        id,
        questions.filter((q) => q.id !== id).map((q) => q.id),
      );
      await load();
    });
  }

  /**
   * Descarga el cuestionario como archivo.
   *
   * Se construye en el navegador y se descarga sin pasar por ningún servidor: el contenido ya
   * está aquí, y darle un rodeo no aportaría nada.
   */
  function exportar(): void {
    if (quiz === null) return;

    const text = serializeExchangeFile(
      {
        title: quiz.title,
        description: quiz.description,
        default_settings: quiz.default_settings,
        questions: questions.map((question) => ({
          type: question.type,
          prompt_md: question.prompt_md,
          hint_md: question.hint_md,
          time_limit_s: question.time_limit_s,
          hint_after_s: question.hint_after_s,
          shuffle_options: question.shuffle_options,
          points_multiplier: question.points_multiplier,
          options: options
            .filter((option) => option.question_id === question.id)
            .map((option) => ({ text_md: option.text_md, is_correct: option.is_correct })),
        })),
      },
      new Date().toISOString(),
    );

    const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = exchangeFileName(quiz.title);
    link.click();
    URL.revokeObjectURL(url);
  }

  const raw: RawQuestion[] = questions.map((question) => ({
    id: question.id,
    position: question.position,
    type: question.type,
    prompt_md: question.prompt_md,
    options: options
      .filter((o) => o.question_id === question.id)
      .map((o) => ({ position: o.position, text_md: o.text_md, is_correct: o.is_correct })),
  }));

  /**
   * El estado que resume toda la pantalla: el más grave de todos.
   *
   * Con veinte filas, lo que importa saber de un vistazo no es qué acaba de pasar, sino si queda
   * algo sin resolver. El detalle de cada fila va en su propia tarjeta.
   */
  const ORDEN: readonly SaveState[] = ['conflict', 'failed', 'saving', 'pending', 'saved'];
  const resumen: SaveState =
    ORDEN.find((estado) => [...estados.values()].includes(estado)) ?? 'saved';
  const enConflicto = [...estados.entries()]
    .filter(([, estado]) => estado === 'conflict')
    .map(([clave]) => clave);

  const readiness = assessQuiz(raw);
  const issues =
    !readiness.playable && readiness.reason === 'invalidQuestions' ? readiness.issues : [];

  /*
   * **La cabecera va en las tres salidas, incluidas las de fallo.**
   *
   * El editor no montaba ninguna: se entraba a modificar un cuestionario y desaparecían el idioma,
   * la cuenta, la salida y cualquier referencia de dónde se estaba. Volver era un enlace pequeño
   * en versales arriba a la izquierda, que además decía «asignaturas» sin decir a cuál.
   */
  const migas: readonly { readonly texto: string; readonly a?: string }[] = [
    { texto: t('subjects.title'), a: '/asignaturas' },
    ...(quiz === null ? [] : [{ texto: t('quizzes.title'), a: `/asignaturas/${quiz.subject_id}` }]),
    { texto: quiz?.title ?? t('quizzes.title') },
  ];

  if (estadoCarga === 'cargando') {
    return (
      <Marco profile={profile} migas={migas}>
        <main id="contenido" className="mx-auto max-w-5xl p-6">
          <p role="status" className="text-micro font-display text-text-dim font-bold uppercase">
            {t('common.loading')}
          </p>
        </main>
      </Marco>
    );
  }

  if (estadoCarga !== 'listo') {
    return (
      <Marco profile={profile} migas={migas}>
        <main id="contenido" className="mx-auto flex max-w-md flex-col gap-4 p-6">
          <Aviso tono="error" interrumpe>
            <p className="font-display font-bold">
              {estadoCarga === 'noExiste' ? t('editor.notFound') : t('common.loadFailedTitle')}
            </p>
            <p className="text-body text-text-dim mt-1">
              {estadoCarga === 'noExiste' ? t('editor.notFoundLead') : t('common.loadFailedLead')}
            </p>
          </Aviso>
          <div className="flex gap-2">
            {estadoCarga === 'fallo' && (
              <Boton variante="primaria" onClick={() => void load()}>
                {t('common.retry')}
              </Boton>
            )}
            <Link
              to="/asignaturas"
              className="text-body font-display rounded-chip border-line inline-flex min-h-11 items-center border-2 px-5 font-bold"
            >
              {t('quizzes.backToSubjects')}
            </Link>
          </div>
        </main>
      </Marco>
    );
  }

  return (
    <Marco profile={profile} migas={migas}>
      <main id="contenido" className="mx-auto max-w-5xl p-6">
        {/* Las migas de la cabecera ya dicen dónde se está y por dónde se vuelve, así que aquí solo
          queda lo que se hace con el cuestionario. */}
        <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
          <div className="flex flex-wrap items-center gap-4">
            <Boton variante="secundaria" onClick={exportar} disabled={quiz === null}>
              {t('import.export')}
            </Boton>
            <IndicadorGuardado state={resumen} />
          </div>
        </div>

        <h1 className="font-display text-h1 font-extrabold text-balance">{quiz?.title ?? ''}</h1>

        {/* Uno por fila en conflicto, y con el número de pregunta: un aviso suelto bajo el título
          no decía de qué pregunta hablaba, y con dos en conflicto solo se veía uno. */}
        {enConflicto.map((clave) => {
          const id = clave.split(':')[1] ?? '';
          const indice = questions.findIndex((q) => q.id === id);
          // Si la fila en conflicto no es una pregunta —una opción, o el propio cuestionario— se
          // nombra el cuestionario, que es lo único que se puede decir con certeza.
          const donde: string =
            indice >= 0 ? t('editor.questionNumber', { number: indice + 1 }) : (quiz?.title ?? '');
          return (
            <AvisoConflicto key={clave} donde={donde} onReload={() => window.location.reload()} />
          );
        })}

        <ResumenValidacion issues={issues} hasQuestions={questions.length > 0} />

        <h2 className="mt-8 text-xl font-semibold">{t('editor.questions')}</h2>

        <ul className="mt-4 flex flex-col gap-4">
          {questions.map((question, index) => (
            <TarjetaPregunta
              key={question.id}
              question={question}
              options={options.filter((o) => o.question_id === question.id)}
              index={index}
              total={questions.length}
              issues={issues.find((i) => i.questionId === question.id)}
              onMove={(i, d) => void move(i, d)}
              onDelete={(id) => setPorBorrar(id)}
              onQuestionChange={changeQuestion}
              onOptionChange={changeOption}
              onOptionAdd={(id) => void anadirOpcion(id)}
              onOptionRemove={(qid, oid) => void quitarOpcion(qid, oid)}
              ocupado={ocupado}
              quizSettings={quiz?.default_settings ?? {}}
            />
          ))}
        </ul>

        <Boton
          variante="primaria"
          className="mt-6"
          cargando={ocupado}
          onClick={() => void addQuestion()}
        >
          {t('editor.addQuestion')}
        </Boton>

        {/* El histórico, aquí y no en una ruta aparte: es donde se busca «cómo fue la última vez»,
          y en su propia pantalla se perdía el contexto de qué cuestionario era. */}
        <HistorialDePartidas quizId={quizId} />

        <DialogoConfirmacion
          abierto={porBorrar !== null}
          destructivo
          titulo={t('editor.delete')}
          cuerpo={t('editor.deleteConfirm')}
          confirmar={t('editor.delete')}
          onConfirmar={() => void remove(porBorrar ?? '')}
          onCancelar={() => setPorBorrar(null)}
        />
      </main>
    </Marco>
  );
}

export { MAX_OPTIONS };
